"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.WebformRenderer = void 0;
const react_1 = __importStar(require("react"));
const WebformSection_1 = require("./WebformSection");
const FamilySection_1 = require("./sections/FamilySection");
const AddressSection_1 = require("./sections/AddressSection");
const EducationSection_1 = require("./sections/EducationSection");
const WorkExperienceSection_1 = require("./sections/WorkExperienceSection");
const DocumentsSection_1 = require("./sections/DocumentsSection");
const TestRegistrationSection_1 = require("./sections/TestRegistrationSection");
const PaymentSection_1 = require("./sections/PaymentSection");
const validation_1 = require("./validation");
const icons_1 = require("./icons");
const WizardChrome_1 = require("./WizardChrome");
/* ─── Definition normalization (legacy label quirks) ──────────────────── */
function normalizeDefinition(def) {
    const sections = def.sections.map((s) => {
        if (!s.fields)
            return s;
        const hasFirst = s.fields.some((f) => f.key === 'firstName');
        const hasLast = s.fields.some((f) => f.key === 'lastName');
        const fields = s.fields.map((f) => {
            if (f.key === 'firstName' && hasFirst && hasLast && /full\s*name/i.test(f.label || '')) {
                return { ...f, label: 'First Name' };
            }
            return f;
        });
        return { ...s, fields };
    });
    return { ...def, sections };
}
/* ─── Validation slicing per section ──────────────────────────────────── */
function errorsInSection(definition, sectionIndex, allErrors) {
    const section = definition.sections[sectionIndex];
    if (!section)
        return {};
    const out = {};
    if (section.fields) {
        for (const f of section.fields) {
            if (allErrors[f.key])
                out[f.key] = allErrors[f.key];
        }
    }
    const prefix = section.type === 'family' ? 'family.' :
        section.type === 'address' ? 'addresses.' :
            section.type === 'education' ? 'education.' :
                section.type === 'workExperience' ? 'workExperience.' : null;
    if (prefix) {
        for (const k of Object.keys(allErrors))
            if (k.startsWith(prefix))
                out[k] = allErrors[k];
    }
    return out;
}
/* ─── Photo upload widget (always shown on Personal step) ─────────────── */
const PhotoUploadWidget = ({ value, onChange, disabled }) => {
    const ref = (0, react_1.useRef)(null);
    const handlePick = () => ref.current?.click();
    const handleFile = (e) => {
        const file = e.target.files?.[0];
        if (!file)
            return;
        const reader = new FileReader();
        reader.onload = () => onChange(typeof reader.result === 'string' ? reader.result : '');
        reader.readAsDataURL(file);
    };
    return (react_1.default.createElement("div", { className: "wf-photo-row" },
        react_1.default.createElement("div", { className: "wf-photo-avatar" }, value
            ? react_1.default.createElement("img", { src: value, alt: "Applicant" })
            : react_1.default.createElement(react_1.default.Fragment, null,
                react_1.default.createElement(icons_1.IconUser, { size: 28, strokeWidth: 1.5 }),
                react_1.default.createElement("span", { className: "wf-photo-avatar-label" }, "PHOTO"))),
        react_1.default.createElement("div", { className: "wf-photo-meta" },
            react_1.default.createElement("div", { className: "wf-photo-title" }, "Applicant Photograph"),
            react_1.default.createElement("div", { className: "wf-photo-desc" }, "Upload a recent passport-size photo. JPG or PNG \u00B7 Max 2MB \u00B7 Minimum 300\u00D7300px"),
            react_1.default.createElement("button", { type: "button", className: "wf-photo-btn", onClick: handlePick, disabled: disabled }, value ? 'Replace Photo' : 'Upload Photo'),
            react_1.default.createElement("input", { ref: ref, type: "file", accept: "image/*", className: "wf-photo-input", onChange: handleFile, disabled: disabled }))));
};
/* ─── Build a reference number for the header pill ────────────────────── */
function makeReference(enquiryId) {
    const year = new Date().getFullYear();
    if (enquiryId)
        return `APP-${year}-${String(enquiryId).padStart(5, '0')}`;
    return `APP-${year}-DRAFT`;
}
/** Legacy `Title` values are stored as raw HTML (`<div style="…">Name</div>`).
 *  Show the readable text instead of the markup. */
function stripTags(html) {
    if (!html)
        return '';
    if (!/[<&]/.test(html))
        return html;
    try {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        return (doc.body.textContent || '').replace(/\s+/g, ' ').trim();
    }
    catch {
        return html;
    }
}
/** Best-effort applicant name from the submitted data. The definition is
 *  tenant-authored JSON, so probe the canonical webform keys and fall back
 *  to the form title when none are present. */
function applicantNameFrom(data) {
    if (!data)
        return '';
    const pick = (...keys) => keys.map((k) => data[k]).find((v) => typeof v === 'string' && v.trim());
    const first = pick('firstName', 'FirstName', 'first_name');
    const last = pick('lastName', 'LastName', 'last_name');
    if (first || last)
        return [first, last].filter(Boolean).join(' ').trim();
    return (pick('name', 'fullName', 'studentName', 'Name', 'applicantName') || '').trim();
}
function initialsFrom(name) {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0)
        return '—';
    return parts.slice(0, 2).map((p) => p.charAt(0).toUpperCase()).join('');
}
/* ─── Shared record header: identity + actions + a nav strip ──────────
 * Used by BOTH the read-only dossier (tabs in the strip) and the wizard
 * (step bar in the strip), so staff never see a stack of three separate
 * bands. `navStrip` is whatever belongs under the identity row. */
const RecordHeader = ({ applicantName, photo, reference, enquiryId, stateLabel, actions, navStrip }) => (react_1.default.createElement("header", { className: "wf-vh" },
    react_1.default.createElement("div", { className: "wf-vh-main" },
        react_1.default.createElement("div", { className: "wf-vh-inner" },
            react_1.default.createElement("div", { className: "wf-vh-identity" },
                react_1.default.createElement("div", { className: "wf-vh-avatar" }, photo ? react_1.default.createElement("img", { src: photo, alt: "" }) : react_1.default.createElement("span", null, initialsFrom(applicantName))),
                react_1.default.createElement("div", { className: "wf-vh-titles" },
                    react_1.default.createElement("h1", { className: "wf-vh-name" }, applicantName || 'Application'),
                    react_1.default.createElement("div", { className: "wf-vh-meta" },
                        react_1.default.createElement("span", { className: "wf-vh-ref" }, reference),
                        enquiryId != null && (react_1.default.createElement(react_1.default.Fragment, null,
                            react_1.default.createElement("span", { className: "wf-vh-sep", "aria-hidden": "true" }),
                            react_1.default.createElement("span", null,
                                "Enquiry ",
                                enquiryId))),
                        react_1.default.createElement("span", { className: "wf-vh-sep", "aria-hidden": "true" }),
                        stateLabel))),
            react_1.default.createElement("div", { className: "wf-vh-actions" }, actions))),
    navStrip && react_1.default.createElement("div", { className: "wf-vh-tabbar" }, navStrip)));
/* ─── Read-only section tabs (scrollspy) ──────────────────────────────── */
const ViewTabs = ({ sections }) => {
    const [activeId, setActiveId] = (0, react_1.useState)(sections[0]?.id || '');
    (0, react_1.useEffect)(() => {
        const onScroll = () => {
            let current = sections[0]?.id || '';
            for (const s of sections) {
                const el = document.getElementById(`section-${s.id}`);
                if (el && el.getBoundingClientRect().top <= 180)
                    current = s.id;
            }
            setActiveId(current);
        };
        onScroll();
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, [sections]);
    const jump = (id) => {
        const el = document.getElementById(`section-${id}`);
        if (!el)
            return;
        const top = el.getBoundingClientRect().top + window.pageYOffset - 140;
        window.scrollTo({ top, behavior: 'smooth' });
    };
    return (react_1.default.createElement("div", { className: "wf-vh-inner" },
        react_1.default.createElement("div", { className: "wf-vh-tabs", role: "tablist", "aria-label": "Application sections" }, sections.map((s) => (react_1.default.createElement("button", { key: s.id, type: "button", role: "tab", "aria-selected": activeId === s.id, className: `wf-vh-tab${activeId === s.id ? ' is-active' : ''}`, onClick: () => jump(s.id) }, s.label))))));
};
/* ─── Status strip: what state is this application actually in? ───────── */
function formatStamp(v) {
    if (!v)
        return null;
    const m = /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}))?/.exec(String(v).trim());
    if (!m || m[1] === '0000')
        return null;
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const day = m[3];
    const mon = months[parseInt(m[2], 10) - 1] ?? m[2];
    const time = m[4] ? ` · ${m[4]}:${m[5]}` : '';
    return `${day} ${mon} ${m[1]}${time}`;
}
const StatusStrip = ({ meta }) => {
    const submitted = formatStamp(meta.finalizedDate);
    const created = formatStamp(meta.creationDate);
    const updated = formatStamp(meta.lastModifiedDate);
    const facts = [];
    if (meta.stageName)
        facts.push({ label: 'Stage', value: meta.stageName });
    if (meta.programName)
        facts.push({ label: 'Programme', value: meta.programName });
    if (submitted)
        facts.push({ label: 'Submitted', value: submitted });
    else if (created)
        facts.push({ label: 'Started', value: created });
    if (updated && updated !== submitted)
        facts.push({ label: 'Last updated', value: updated });
    if (facts.length === 0 && meta.isDraft === undefined)
        return null;
    return (react_1.default.createElement("div", { className: "wf-status-strip" },
        react_1.default.createElement("span", { className: `wf-status-pill${meta.isDraft ? ' is-draft' : ' is-submitted'}` },
            react_1.default.createElement("span", { className: "wf-status-dot" }),
            meta.isDraft ? 'Draft' : 'Submitted'),
        facts.map((f) => (react_1.default.createElement("div", { key: f.label, className: "wf-status-fact" },
            react_1.default.createElement("span", { className: "wf-status-fact-label" }, f.label),
            react_1.default.createElement("span", { className: "wf-status-fact-value" }, f.value))))));
};
/* ─── Renderer ─────────────────────────────────────────────────────────── */
const WebformRenderer = ({ definition: rawDefinition, lookups, initialData, onSubmit, onSaveDraft, readOnly = false, isFinalizedEdit = false, enquiryId, successActions, headerActions, }) => {
    const definition = (0, react_1.useMemo)(() => normalizeDefinition(rawDefinition), [rawDefinition]);
    const [formData, setFormData] = (0, react_1.useState)(initialData || {});
    const [errors, setErrors] = (0, react_1.useState)({});
    const [submitting, setSubmitting] = (0, react_1.useState)(false);
    const [submitError, setSubmitError] = (0, react_1.useState)(null);
    const [submitSuccess, setSubmitSuccess] = (0, react_1.useState)(null);
    const [declarationAccepted, setDeclarationAccepted] = (0, react_1.useState)(false);
    const wizardSteps = (0, react_1.useMemo)(() => (0, WizardChrome_1.buildWizardSteps)(definition), [definition]);
    const reviewIndex = wizardSteps.length - 1;
    const [stepIndex, setStepIndex] = (0, react_1.useState)(0);
    // Auto-dismiss the submit-error banner after 5s so it doesn't linger
    // once the user has jumped to fix the flagged section. The red step
    // bubbles stay put until the fields are actually fixed — the banner is
    // just the initial nudge.
    (0, react_1.useEffect)(() => {
        if (!submitError)
            return;
        const t = setTimeout(() => setSubmitError(null), 5000);
        return () => clearTimeout(t);
    }, [submitError]);
    // Wizard step indices whose section currently has validation errors.
    // Drives the red-bubble state on StepProgressBar. Placed here (above
    // any conditional returns) so React's hook-order rule is preserved.
    const erroredStepSet = (0, react_1.useMemo)(() => {
        const set = new Set();
        if (!errors || Object.keys(errors).length === 0)
            return set;
        for (let i = 0; i <= reviewIndex; i++) {
            const idx = (0, WizardChrome_1.sectionIndexForStep)(definition, i);
            if (idx >= 0 && Object.keys(errorsInSection(definition, idx, errors)).length > 0) {
                set.add(i);
            }
        }
        return set;
    }, [definition, errors, reviewIndex]);
    const handleChange = (0, react_1.useCallback)((key, value) => {
        setFormData((prev) => ({ ...prev, [key]: value }));
        setErrors((prev) => {
            if (!prev[key])
                return prev;
            const next = { ...prev };
            delete next[key];
            return next;
        });
    }, []);
    const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });
    const handleNext = (0, react_1.useCallback)(() => {
        const sectionIdx = (0, WizardChrome_1.sectionIndexForStep)(definition, stepIndex);
        if (sectionIdx >= 0) {
            const all = (0, validation_1.validateForm)(definition, formData);
            const local = errorsInSection(definition, sectionIdx, all);
            if (Object.keys(local).length > 0) {
                setErrors(local);
                const firstKey = Object.keys(local)[0];
                const el = document.querySelector(`[name="${firstKey}"], [name="${firstKey}-day"]`);
                if (el)
                    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                return;
            }
            setErrors({});
        }
        setStepIndex(stepIndex + 1);
        scrollToTop();
    }, [stepIndex, definition, formData]);
    const handleBack = (0, react_1.useCallback)(() => {
        if (stepIndex === 0)
            return;
        setStepIndex(stepIndex - 1);
        setErrors({});
        scrollToTop();
    }, [stepIndex]);
    const handleSubmit = (0, react_1.useCallback)(async (e) => {
        e.preventDefault();
        setSubmitError(null);
        const validationErrors = (0, validation_1.validateForm)(definition, formData);
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            // Jump to the first wizard step with errors
            for (let i = 0; i <= reviewIndex; i++) {
                const idx = (0, WizardChrome_1.sectionIndexForStep)(definition, i);
                if (idx >= 0 && Object.keys(errorsInSection(definition, idx, validationErrors)).length > 0) {
                    setStepIndex(i);
                    scrollToTop();
                    break;
                }
            }
            setSubmitError('Some sections need your attention. Please review and complete them.');
            return;
        }
        setSubmitting(true);
        try {
            await onSubmit(formData);
            const msg = definition.confirmationMessage?.success || 'Thank you. Your application has been received.';
            setSubmitSuccess(msg);
        }
        catch (err) {
            setSubmitError(err?.message || 'An error occurred while submitting the form.');
        }
        finally {
            setSubmitting(false);
        }
    }, [definition, formData, onSubmit, reviewIndex]);
    const handleSaveDraft = (0, react_1.useCallback)(async () => {
        if (!onSaveDraft)
            return;
        setSubmitting(true);
        try {
            await onSaveDraft(formData);
        }
        finally {
            setSubmitting(false);
        }
    }, [formData, onSaveDraft]);
    /** Update-from-any-step handler (finalized-edit mode). Runs full-form
     *  validation like a real submit; on success calls onSubmit with the
     *  current data. If a section has errors, jumps the wizard to that
     *  step so the user can fix them. */
    const handleUpdate = (0, react_1.useCallback)(async () => {
        setSubmitError(null);
        const validationErrors = (0, validation_1.validateForm)(definition, formData);
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            for (let i = 0; i <= reviewIndex; i++) {
                const idx = (0, WizardChrome_1.sectionIndexForStep)(definition, i);
                if (idx >= 0 && Object.keys(errorsInSection(definition, idx, validationErrors)).length > 0) {
                    setStepIndex(i);
                    scrollToTop();
                    break;
                }
            }
            setSubmitError('Some sections need your attention. Please review and complete them.');
            return;
        }
        setSubmitting(true);
        try {
            await onSubmit(formData);
            const msg = definition.confirmationMessage?.success || 'Application updated.';
            setSubmitSuccess(msg);
        }
        catch (err) {
            setSubmitError(err?.message || 'An error occurred while updating the form.');
        }
        finally {
            setSubmitting(false);
        }
    }, [definition, formData, onSubmit, reviewIndex]);
    /* ─── Success view ─── */
    if (submitSuccess) {
        return (react_1.default.createElement("div", { className: "wf" },
            react_1.default.createElement("div", { className: "wf-content" },
                react_1.default.createElement("div", { className: "section-card" },
                    react_1.default.createElement("div", { className: "success-message" },
                        react_1.default.createElement("div", { className: "success-icon" },
                            react_1.default.createElement(icons_1.IconCheck, { size: 28, strokeWidth: 2.5 })),
                        react_1.default.createElement("div", { className: "success-title" }, "Application Submitted"),
                        react_1.default.createElement("div", { className: "success-text" }, submitSuccess),
                        successActions && react_1.default.createElement("div", { className: "success-actions" }, successActions))))));
    }
    /* Header identity — shared by the wizard and the read-only dossier.
     * `definition.title` is legacy HTML, so strip the markup before display. */
    const institution = stripTags(definition.title) || stripTags(definition.name) || 'Application Portal';
    const referenceId = enquiryId ?? initialData?.enquiryId ?? initialData?.EnquiryId ?? initialData?.id ?? null;
    const reference = makeReference(referenceId);
    const enquiryMeta = initialData?._meta || null;
    /* ─── Read-only / staff view — single-page application dossier ─── */
    if (readOnly) {
        const navSections = definition.sections.map((s) => ({
            id: s.id,
            label: s.title ? stripTags(s.title) : (0, WizardChrome_1.stepLabelFor)(s),
        }));
        return (react_1.default.createElement("div", { className: "wf wf-view-mode" },
            react_1.default.createElement(RecordHeader, { applicantName: applicantNameFrom(formData) || institution, photo: typeof formData.photo === 'string' ? formData.photo : undefined, reference: reference, enquiryId: referenceId, stateLabel: react_1.default.createElement("span", { className: "wf-vh-state" },
                    react_1.default.createElement(icons_1.IconLock, { size: 10, strokeWidth: 2.6 }),
                    "Read only"), actions: react_1.default.createElement(react_1.default.Fragment, null,
                    react_1.default.createElement("button", { type: "button", className: "wf-vh-btn", onClick: () => window.print(), title: "Print this application" },
                        react_1.default.createElement(icons_1.IconPrinter, { size: 14, strokeWidth: 2 }),
                        react_1.default.createElement("span", null, "Print")),
                    headerActions), navStrip: navSections.length > 1 ? react_1.default.createElement(ViewTabs, { sections: navSections }) : undefined }),
            react_1.default.createElement("div", { className: "wf-content" },
                enquiryMeta && react_1.default.createElement(StatusStrip, { meta: enquiryMeta }),
                react_1.default.createElement("form", { noValidate: true }, definition.sections.map((section) => (react_1.default.createElement(SectionCard, { key: section.id, section: section, data: formData, errors: errors, onChange: handleChange, disabled: true, lookups: lookups })))))));
    }
    /* ─── Wizard view ─── */
    const isReview = stepIndex === reviewIndex;
    const currentSectionIdx = (0, WizardChrome_1.sectionIndexForStep)(definition, stepIndex);
    const currentSection = currentSectionIdx >= 0 ? definition.sections[currentSectionIdx] : null;
    const extrasSection = (0, WizardChrome_1.findExtrasSection)(definition);
    // Pull the "remarks" field out of extras for the declaration card
    const remarksField = extrasSection?.fields?.find((f) => f.key.startsWith('remark_') || /remark/i.test(f.label || '')) || null;
    // Other extras fields (couponCode, achievements, etc.) stay on the Additional Info card
    const otherExtras = extrasSection?.fields?.filter((f) => f !== remarksField) || [];
    const stepBar = (react_1.default.createElement(WizardChrome_1.StepProgressBar, { steps: wizardSteps, activeIndex: stepIndex, allCompleted: isFinalizedEdit, erroredSteps: erroredStepSet, onJumpToStep: isFinalizedEdit ? (i) => {
            setStepIndex(i);
            scrollToTop();
        } : undefined }));
    const wizardName = applicantNameFrom(formData);
    return (react_1.default.createElement("div", { className: "wf" },
        referenceId != null ? (react_1.default.createElement(RecordHeader, { applicantName: wizardName || institution, photo: typeof formData.photo === 'string' ? formData.photo : undefined, reference: reference, enquiryId: referenceId, stateLabel: react_1.default.createElement("span", { className: "wf-vh-state is-editing" },
                react_1.default.createElement(icons_1.IconPencil, { size: 10, strokeWidth: 2.6 }),
                isFinalizedEdit ? 'Editing' : 'Draft'), actions: headerActions, navStrip: stepBar })) : (react_1.default.createElement(react_1.default.Fragment, null,
            react_1.default.createElement(WizardChrome_1.BrandedHeader, { institution: institution, reference: reference }),
            stepBar)),
        react_1.default.createElement("div", { className: "wf-content" },
            react_1.default.createElement(WizardChrome_1.RequiredFieldsHint, null),
            react_1.default.createElement("form", { onSubmit: handleSubmit, noValidate: true },
                !isReview && currentSection && (react_1.default.createElement(SectionCard, { section: currentSection, data: formData, errors: errors, onChange: handleChange, disabled: false, lookups: lookups, showPhotoWidget: currentSection.id === 'basic' })),
                isReview && otherExtras.length > 0 && extrasSection && (react_1.default.createElement(SectionCard, { section: { ...extrasSection, fields: otherExtras }, data: formData, errors: errors, onChange: handleChange, disabled: false, lookups: lookups })),
                isReview && (react_1.default.createElement(WizardChrome_1.DeclarationCard, { remarksField: remarksField ? { key: remarksField.key, label: remarksField.label || 'Remarks' } : null, remarksValue: remarksField ? (formData[remarksField.key] || '') : '', onRemarksChange: (v) => remarksField && handleChange(remarksField.key, v), declarationAccepted: declarationAccepted, onDeclarationChange: setDeclarationAccepted, onBack: handleBack, onSaveDraft: onSaveDraft && definition.config?.saveDraft?.enabled ? handleSaveDraft : undefined, submitting: submitting, submitError: submitError, isFinalizedEdit: isFinalizedEdit })),
                !isReview && submitError && (react_1.default.createElement("div", { className: "error-alert" },
                    react_1.default.createElement(icons_1.IconAlertCircle, { size: 16 }),
                    react_1.default.createElement("span", null, submitError))),
                !isReview && (react_1.default.createElement(WizardChrome_1.WizardNav, { step: stepIndex, onBack: handleBack, onNext: handleNext, onSaveDraft: onSaveDraft && definition.config?.saveDraft?.enabled ? handleSaveDraft : undefined, submitting: submitting, isFinalizedEdit: isFinalizedEdit, onUpdate: isFinalizedEdit ? handleUpdate : undefined }))))));
};
exports.WebformRenderer = WebformRenderer;
const SectionCard = ({ section, data, errors, onChange, disabled, lookups, showPhotoWidget, }) => {
    const Icon = (0, icons_1.sectionIconFor)(section.id);
    const tint = (0, icons_1.sectionTintFor)(section.id);
    // Strip any data-driven `photo` field from basic when the dedicated widget is showing
    const renderedSection = (showPhotoWidget && section.fields)
        ? { ...section, fields: section.fields.filter((f) => f.key !== 'photo') }
        : section;
    const isGeneric = !renderedSection.type || renderedSection.type === 'generic';
    const isEmptyGeneric = isGeneric && !renderedSection.fields?.length;
    const inner = (() => {
        if (isEmptyGeneric) {
            return react_1.default.createElement("div", { className: "wf-section-empty" }, "No information provided.");
        }
        const props = { data, errors, onChange, disabled, lookups };
        switch (renderedSection.type) {
            case 'family': return react_1.default.createElement(FamilySection_1.FamilySection, { section: renderedSection, ...props });
            case 'address': return react_1.default.createElement(AddressSection_1.AddressSection, { section: renderedSection, ...props });
            case 'education': return react_1.default.createElement(EducationSection_1.EducationSection, { section: renderedSection, ...props });
            case 'workExperience': return react_1.default.createElement(WorkExperienceSection_1.WorkExperienceSection, { section: renderedSection, ...props });
            case 'documents': return react_1.default.createElement(DocumentsSection_1.DocumentsSection, { section: renderedSection, ...props });
            case 'testRegistration': return react_1.default.createElement(TestRegistrationSection_1.TestRegistrationSection, { section: renderedSection, ...props });
            case 'payment': return react_1.default.createElement(PaymentSection_1.PaymentSection, { section: renderedSection, ...props });
            default: return react_1.default.createElement(WebformSection_1.WebformSection, { section: renderedSection, ...props });
        }
    })();
    return (react_1.default.createElement("div", { className: "section-card", id: `section-${section.id}` },
        react_1.default.createElement("div", { className: "section-header" },
            react_1.default.createElement("div", { className: "section-icon", style: { background: tint.bg, color: tint.stroke } },
                react_1.default.createElement(Icon, { size: 16 })),
            react_1.default.createElement("div", { className: "section-heading" },
                react_1.default.createElement("div", { className: "section-title" }, stripTags(section.title) || (0, WizardChrome_1.stepLabelFor)(section)),
                section.description && (react_1.default.createElement("div", { className: "section-subtitle" }, stripTags(section.description))))),
        react_1.default.createElement("div", { className: "section-body" },
            showPhotoWidget && (react_1.default.createElement(PhotoUploadWidget, { value: data.photo, onChange: (v) => onChange('photo', v), disabled: disabled })),
            inner)));
};
